import { useState } from "react";
import {
  Loader,
  ChevronUp,
  ChevronDown,
  Settings,
  AlertCircle,
} from "lucide-react";
import { useParams } from "react-router-dom";
import { useAudit } from "../../contexts/auditContext";
import { AuditUploadSampleList } from "../../pages/audit/auditSampleTest/components/AuditUploadSampleList";
import { AuditUploadAdditionalSamples } from "../../pages/audit/auditSampleTest/components/AuditUploadAdditionalSamples";
import { AuditSampleList } from "../../pages/audit/auditSampleTest/components/AuditSampleList";
import { AuditDocumentVerification } from "../../pages/audit/auditSampleTest/components/AuditDocumentVerification";
import { Button } from "../ui/button";
import { Alert, AlertTitle, AlertDescription } from "../ui/alert";
import { ProcedureAttributesModal } from "./ProcedureAttributesModal";
import { useProcedureSession } from "../../hooks/audit/useProcedureSession";
import { useProcedureSessionSourceRows } from "../../hooks/audit/useProcedureSessionSourceRows";
import { useControlSessionEnvelope } from "../../hooks/audit/useControlSessionEnvelope";
import { LoadingSpinner } from "../ui/loadingSpinner/loadingSpinner";

interface AuditSessionContentProps {
  procedureSessionId: string;
  currentIndex?: number;
  totalSessions?: number;
  onNavigate?: (index: number) => void;
  isDocumentsProcessing?: boolean;
}

export const AuditSessionContent = ({
  procedureSessionId,
  currentIndex,
  totalSessions,
  onNavigate,
  isDocumentsProcessing,
}: AuditSessionContentProps) => {
  const { controlSession, sessionKind } = useAudit();
  const { controlSessionId } = useParams<{ controlSessionId: string }>();
  // Slim card-list source for the Support tab (lazy load).
  const {
    data: sourceRowsPage,
    error: sourceRowsError,
    refetch: refetchSourceRows,
  } = useProcedureSessionSourceRows(procedureSessionId);
  // Parallel full-tree fetch kept for legacy consumers
  // (`AuditUploadSampleList`, `AuditUploadAdditionalSamples`,
  // `ProcedureAttributesModal`, and the `auditSession` slot that
  // `AuditSampleList` / `AuditDocumentVerification` thread to their own
  // legacy children + upload helper). Removing this is out of scope.
  const {
    data: auditSession,
    error: auditSessionError,
    refetch: refetchAuditSession,
  } = useProcedureSession(procedureSessionId);
  const {
    data: controlSessionEnvelope,
    error: controlSessionEnvelopeError,
    refetch: refetchControlSessionEnvelope,
  } = useControlSessionEnvelope(controlSessionId, sessionKind);
  const [attributesModalOpen, setAttributesModalOpen] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const isSessionComplete = controlSession?.status === "complete";
  const sourceRows = sourceRowsPage?.sourceRows ?? [];
  const hasSamples = sourceRows.length > 0;
  const procedureEnvelope = controlSessionEnvelope?.procedureSessions.find(
    (ps) => ps.id === procedureSessionId,
  );
  // Disable the button while retries are in flight so rapid clicks can't fire
  // (and re-report to Sentry) duplicate refetches; re-enabled once all settle.
  const retryFailedFetches = async () => {
    setIsRetrying(true);
    try {
      const retries: Promise<void>[] = [];
      if (sourceRowsError) retries.push(refetchSourceRows());
      if (auditSessionError) retries.push(refetchAuditSession());
      if (controlSessionEnvelopeError)
        retries.push(refetchControlSessionEnvelope());
      await Promise.all(retries);
    } finally {
      setIsRetrying(false);
    }
  };

  const showNavigation =
    currentIndex !== undefined &&
    totalSessions !== undefined &&
    totalSessions > 1 &&
    onNavigate;

  // Gate the entire body on `controlSessionId !== undefined` per the cross-repo
  // decision — never invoke `useControlSessionEnvelope("")` or pass an empty
  // controlSessionId into children.
  if (controlSessionId === undefined) {
    return (
      <div className="mb-8 flex items-center justify-center py-6">
        <LoadingSpinner />
      </div>
    );
  }

  // A required fetch failed AND that same query has no cached data to fall back
  // on — render an inline error + retry instead of the infinite spinner below
  // (MS-2421). Each error is paired with its OWN query's missing data so that
  // (a) a failed *background* refetch, which keeps its cached data, still renders
  // the stale-but-usable body (the toast surfaced it), and (b) one query's error
  // never masks a sibling that is merely still loading. `procedureEnvelope` is
  // derived from the (shared) control-session envelope, so it pairs with that
  // query's error.
  if (
    (sourceRowsError && !sourceRowsPage) ||
    (auditSessionError && !auditSession) ||
    (controlSessionEnvelopeError && !procedureEnvelope)
  ) {
    return (
      <div className="mb-8 flex justify-center py-6">
        <Alert variant="destructive" className="max-w-md">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Unable to load this session</AlertTitle>
          <AlertDescription className="mt-2">
            Something went wrong while loading this data. Please try again.
            <div className="mt-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => void retryFailedFetches()}
                disabled={isRetrying}
              >
                {isRetrying ? (
                  <>
                    <Loader className="mr-2 h-4 w-4 animate-spin" />
                    Retrying…
                  </>
                ) : (
                  "Retry"
                )}
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Gate on data presence, not `isLoading`. A background refetch (e.g. after
  // a sample upload invalidates the source-rows prefix) flips `isLoading` to
  // true while the cached `sourceRowsPage` data is preserved — gating on the
  // loading flag would unmount the entire support tree and reset child state
  // (selected sample, scroll position) for what should be an invisible refresh.
  if (!sourceRowsPage || !auditSession || !procedureEnvelope) {
    return (
      <div className="mb-8 flex items-center justify-center py-6">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="text-sm font-semibold text-font-primary">
            {auditSession.procedure.name}
          </div>
          {!isSessionComplete && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => setAttributesModalOpen(true)}
              title="Manage attributes"
            >
              <Settings className="h-4 w-4" />
            </Button>
          )}
        </div>
        {showNavigation && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">
              {currentIndex + 1} of {totalSessions}
            </span>
            <div className="flex gap-1">
              <Button
                size="icon"
                variant="outline"
                className="h-7 w-7"
                onClick={() => onNavigate(currentIndex - 1)}
                disabled={currentIndex === 0}
                title="Previous procedure"
              >
                <ChevronUp className="h-4 w-4" />
              </Button>
              <Button
                size="icon"
                variant="outline"
                className="h-7 w-7"
                onClick={() => onNavigate(currentIndex + 1)}
                disabled={currentIndex === totalSessions - 1}
                title="Next procedure"
              >
                <ChevronDown className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
      {!hasSamples ? (
        <div>
          {!isSessionComplete && (
            <AuditUploadSampleList auditSession={auditSession} />
          )}
        </div>
      ) : (
        <>
          <div>
            {!isSessionComplete &&
              auditSession?.procedure.procedureBasis == "sample" && (
                <div className="flex flex-row gap-2 mb-2">
                  <AuditUploadAdditionalSamples auditSession={auditSession} />
                </div>
              )}
            {isDocumentsProcessing && (
              <div className="text-sm text-font-secondary flex flex-row gap-2 items-center my-2">
                <Loader className="animate-spin" />
                Processing documents...
              </div>
            )}
            {procedureEnvelope.procedure.procedureBasis === "sample" ? (
              <AuditSampleList
                sourceRows={sourceRows}
                procedureSessionId={procedureSessionId}
                controlSessionId={controlSessionId}
                auditSession={auditSession}
              />
            ) : (
              sourceRows[0]?.id && (
                <AuditDocumentVerification
                  procedureSessionId={procedureSessionId}
                  controlSessionId={controlSessionId}
                  auditSourceRowId={sourceRows[0].id}
                  auditSession={auditSession}
                />
              )
            )}
          </div>
        </>
      )}

      <ProcedureAttributesModal
        open={attributesModalOpen}
        onOpenChange={setAttributesModalOpen}
        auditSession={auditSession}
      />
    </div>
  );
};
